import { Button, Card, Chip } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type CostSettingsRow, type CostSnapshotRow } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";
import { formatRial } from "./format.js";

interface MenuItemRow {
  id: string;
  name: string;
  kind?: string;
}

export function CostTab() {
  const foods = useQuery({
    queryKey: ["menu", "items", "food"],
    queryFn: () => api<MenuItemRow[]>("/menu/items?kind=food"),
  });
  const foodMap = new Map((foods.data ?? []).map((f) => [f.id, f.name]));

  const [date, setDate] = useState("1405-08-01");
  const [showSettings, setShowSettings] = useState(false);
  const [calcFoodId, setCalcFoodId] = useState("");

  const settings = useQuery({
    queryKey: ["inventory", "cost", "settings"],
    queryFn: () => api<CostSettingsRow>("/inventory/cost/settings"),
  });

  const snapshots = useQuery({
    queryKey: ["inventory", "cost", "snapshots", date],
    queryFn: () => api<CostSnapshotRow[]>(`/inventory/cost/snapshots?businessDate=${date}`),
  });

  // Settings form states
  const [fixedMode, setFixedMode] = useState<"per_portion" | "monthly_share">("per_portion");
  const [packagingCost, setPackagingCost] = useState("15000");
  const [breadCost, setBreadCost] = useState("10000");
  const [staffShare, setStaffShare] = useState("80000");
  const [overheadShare, setOverheadShare] = useState("40000");
  const [monthlyStaffPool, setMonthlyStaffPool] = useState("2000000000");
  const [monthlyOverheadPool, setMonthlyOverheadPool] = useState("1000000000");
  const [monthlyPortionsEstimate, setMonthlyPortionsEstimate] = useState("25000");
  const [thresholdPerMille, setThresholdPerMille] = useState("50");

  const openSettings = () => {
    if (settings.data) {
      setFixedMode(settings.data.fixedMode);
      setPackagingCost(String(settings.data.packagingCostRial));
      setBreadCost(String(settings.data.breadCostRial));
      setStaffShare(String(settings.data.staffSharePerPortionRial));
      setOverheadShare(String(settings.data.overheadSharePerPortionRial));
      setMonthlyStaffPool(String(settings.data.monthlyStaffPoolRial));
      setMonthlyOverheadPool(String(settings.data.monthlyOverheadPoolRial));
      setMonthlyPortionsEstimate(String(settings.data.monthlyPortionsEstimate));
      setThresholdPerMille(String(settings.data.reconciliationThresholdPerMille));
    }
    setShowSettings(true);
  };

  const saveSettingsAction = useAction(async () => {
    await api("/inventory/cost/settings", {
      method: "PUT",
      body: {
        fixedMode,
        packagingCostRial: parseInt(packagingCost, 10),
        breadCostRial: parseInt(breadCost, 10),
        staffSharePerPortionRial: parseInt(staffShare, 10),
        overheadSharePerPortionRial: parseInt(overheadShare, 10),
        monthlyStaffPoolRial: parseInt(monthlyStaffPool, 10),
        monthlyOverheadPoolRial: parseInt(monthlyOverheadPool, 10),
        monthlyPortionsEstimate: parseInt(monthlyPortionsEstimate, 10),
        reconciliationThresholdPerMille: parseInt(thresholdPerMille, 10),
      },
    });
    setShowSettings(false);
  }, [["inventory", "cost", "settings"]]);

  const calculateAction = useAction(async () => {
    const fId = calcFoodId || (foods.data?.[0]?.id ?? "");
    if (!fId) throw new Error("لطفاً یک غذا انتخاب کنید");

    await api("/inventory/cost/snapshots", {
      method: "POST",
      body: {
        businessDate: date,
        menuItemId: fId,
      },
    });
  }, [["inventory", "cost", "snapshots"]]);

  const snapshotItems = snapshots.data ?? [];

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <div className="cluster" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <div className="cluster" style={{ gap: "var(--s3)", alignItems: "center" }}>
          <div className="field">
            <label>تاریخ کاری</label>
            <input
              type="text"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
              }}
              className="input"
              dir="ltr"
              style={{ width: "130px" }}
            />
          </div>

          <div className="field">
            <label>محاسبه بهای غذا</label>
            <select
              value={calcFoodId || (foods.data?.[0]?.id ?? "")}
              onChange={(e) => {
                setCalcFoodId(e.target.value);
              }}
              className="input"
            >
              {(foods.data ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: "auto" }}>
            <Button
              variant="primary"
              onClick={() => {
                calculateAction.run(undefined);
              }}
              disabled={calculateAction.busy}
            >
              {calculateAction.busy ? "در حال محاسبه..." : "محاسبه و انجماد بهای روز ❄️"}
            </Button>
          </div>
        </div>

        <Button
          variant="default"
          onClick={() => {
            openSettings();
          }}
        >
          تنظیمات بهای تمام‌شده ⚙️
        </Button>
      </div>

      <ErrorLine error={calculateAction.error} />

      {showSettings && (
        <Card>
          <div className="stack" style={{ gap: "var(--s3)" }}>
            <h3 className="t-card">تنظیمات بهای تمام‌شده (ویژه مدیر)</h3>
            <div className="cluster" style={{ gap: "var(--s3)" }}>
              <div className="field">
                <label>روش سرشکن هزینه‌های ثابت</label>
                <select
                  value={fixedMode}
                  onChange={(e) => {
                    setFixedMode(e.target.value as "per_portion" | "monthly_share");
                  }}
                  className="input"
                >
                  <option value="per_portion">مبلغ ثابت به‌ازای هر پرس (پیش‌فرض)</option>
                  <option value="monthly_share">مبلغ ماهانه تقسیم بر تعداد پرس ماه</option>
                </select>
              </div>

              <div className="field">
                <label>بهای بسته‌بندی/ظرف (ریال)</label>
                <input
                  type="number"
                  value={packagingCost}
                  onChange={(e) => {
                    setPackagingCost(e.target.value);
                  }}
                  className="input"
                  dir="ltr"
                />
              </div>

              <div className="field">
                <label>بهای نان (ریال)</label>
                <input
                  type="number"
                  value={breadCost}
                  onChange={(e) => {
                    setBreadCost(e.target.value);
                  }}
                  className="input"
                  dir="ltr"
                />
              </div>
            </div>

            {fixedMode === "per_portion" ? (
              <div className="cluster" style={{ gap: "var(--s3)" }}>
                <div className="field">
                  <label>سهم پرسنل و کسورات قانونی (ریال/پرس)</label>
                  <input
                    type="number"
                    value={staffShare}
                    onChange={(e) => {
                      setStaffShare(e.target.value);
                    }}
                    className="input"
                    dir="ltr"
                  />
                </div>

                <div className="field">
                  <label>سهم اجاره و انشعابات (ریال/پرس)</label>
                  <input
                    type="number"
                    value={overheadShare}
                    onChange={(e) => {
                      setOverheadShare(e.target.value);
                    }}
                    className="input"
                    dir="ltr"
                  />
                </div>
              </div>
            ) : (
              <div className="cluster" style={{ gap: "var(--s3)" }}>
                <div className="field">
                  <label>استخر ماهانه پرسنل (ریال)</label>
                  <input
                    type="number"
                    value={monthlyStaffPool}
                    onChange={(e) => {
                      setMonthlyStaffPool(e.target.value);
                    }}
                    className="input"
                    dir="ltr"
                  />
                </div>

                <div className="field">
                  <label>استخر ماهانه اجاره و انشعابات (ریال)</label>
                  <input
                    type="number"
                    value={monthlyOverheadPool}
                    onChange={(e) => {
                      setMonthlyOverheadPool(e.target.value);
                    }}
                    className="input"
                    dir="ltr"
                  />
                </div>

                <div className="field">
                  <label>تخمین پرس ماهانه</label>
                  <input
                    type="number"
                    value={monthlyPortionsEstimate}
                    onChange={(e) => {
                      setMonthlyPortionsEstimate(e.target.value);
                    }}
                    className="input"
                    dir="ltr"
                  />
                </div>
              </div>
            )}

            <div className="field">
              <label>آستانه هشدار تطابق تولید (در هزار - ۵۰ = ۵٪)</label>
              <input
                type="number"
                value={thresholdPerMille}
                onChange={(e) => {
                  setThresholdPerMille(e.target.value);
                }}
                className="input"
                dir="ltr"
                style={{ width: "120px" }}
              />
            </div>

            <ErrorLine error={saveSettingsAction.error} />

            <div className="cluster" style={{ justifyContent: "flex-end", gap: "var(--s2)" }}>
              <Button
                onClick={() => {
                  setShowSettings(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  saveSettingsAction.run(undefined);
                }}
                disabled={saveSettingsAction.busy}
              >
                {saveSettingsAction.busy ? "در حال ذخیره..." : "ذخیره تنظیمات"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <table className="table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>نام غذا</th>
              <th>مواد اولیه (ریال)</th>
              <th>بسته‌بندی (ریال)</th>
              <th>نان (ریال)</th>
              <th>سهم هزینه‌های ثابت (ریال)</th>
              <th>بهای تمام‌شده کل (ریال)</th>
              <th>وضعیت انجماد</th>
            </tr>
          </thead>
          <tbody>
            {snapshotItems.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "var(--muted)" }}>
                  هیچ بهای منجمدشده‌ای برای این تاریخ ثبت نشده است
                </td>
              </tr>
            ) : (
              snapshotItems.map((snap) => (
                <tr key={snap.id}>
                  <td>
                    <strong>{foodMap.get(snap.menuItemId) ?? snap.menuItemId.slice(0, 8)}</strong>
                  </td>
                  <td>{formatRial(snap.materialsCostRial - snap.packagingCostRial - snap.breadCostRial)}</td>
                  <td>{formatRial(snap.packagingCostRial)}</td>
                  <td>{formatRial(snap.breadCostRial)}</td>
                  <td>{formatRial(snap.fixedShareRial)}</td>
                  <td style={{ fontWeight: "bold", fontSize: "1.05em", color: "var(--primary)" }}>
                    {formatRial(snap.totalCostRial)}
                  </td>
                  <td>
                    <Chip tone="blue">منجمد شده ❄️</Chip>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
